import { db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';

/**
 * @file accessLogs.ts
 * @description Servicios para gestión de logs de acceso y auditoría
 */

export interface AccessLog {
  id: string;
  webId: string;
  
  // Información del acceso
  accessType: 'scan' | 'view' | 'emergency' | 'admin' | 'claim';
  userAgent?: string;
  ipAddress?: string;
  location?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    address?: string;
  };
  
  // Contexto
  accessedBy?: string; // UID si está autenticado
  emergencyContext?: boolean;
  referrer?: string;
  
  // Metadata
  timestamp: Timestamp;
  duration?: number; // Tiempo en la página (segundos)
}

export interface UserActionLog {
  id: string;
  userId: string;
  
  // Acción realizada
  action: string;
  resource: string;
  resourceId?: string;
  
  // Detalles
  details?: Record<string, any>;
  
  // Contexto
  ipAddress?: string;
  userAgent?: string;
  
  // Metadata
  timestamp: Timestamp;
  success: boolean;
  errorMessage?: string;
}

// Referencias a colecciones
const accessLogsCollection = collection(db, 'access_logs');
const userActionLogsCollection = collection(db, 'user_action_logs');

/**
 * Obtener información de geolocalización
 */
const getCurrentLocation = (): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      () => resolve(null),
      { timeout: 5000, enableHighAccuracy: false }
    );
  });
};

/**
 * Obtener dirección aproximada desde coordenadas
 */
const getAddressFromCoordinates = async (lat: number, lng: number): Promise<string | undefined> => {
  try {
    // En producción, usar un servicio de geocodificación como Google Maps API
    // Por ahora, retornar coordenadas formateadas
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } catch (error) {
    console.warn('Error getting address:', error);
    return undefined;
  }
};

/**
 * Registrar acceso a WebID
 */
export const logWebIDAccess = async (
  webId: string,
  accessType: AccessLog['accessType'],
  options: {
    userId?: string;
    emergencyContext?: boolean;
    referrer?: string;
    includeLocation?: boolean;
  } = {}
): Promise<void> => {
  try {
    const logRef = doc(accessLogsCollection);
    
    // Obtener información del navegador
    const userAgent = navigator.userAgent;
    
    // Obtener ubicación si se solicita
    let location: AccessLog['location'] | undefined;
    if (options.includeLocation) {
      try {
        const position = await getCurrentLocation();
        if (position) {
          const address = await getAddressFromCoordinates(
            position.coords.latitude,
            position.coords.longitude
          );
          
          location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            address,
          };
        }
      } catch (error) {
        console.warn('Error getting location for access log:', error);
      }
    }

    // Crear log de acceso
    const accessLog: AccessLog = {
      id: logRef.id,
      webId,
      accessType,
      userAgent,
      location,
      accessedBy: options.userId,
      emergencyContext: options.emergencyContext || false,
      referrer: options.referrer || document.referrer || undefined,
      timestamp: Timestamp.now(),
    };

    await setDoc(logRef, accessLog);

    console.log('✅ Access log created:', logRef.id);
  } catch (error) {
    // Solo mostrar error si no es un problema de permisos esperado
    if (error instanceof Error && !error.message.includes('insufficient permissions')) {
      console.error('Error logging WebID access:', error);
    }
    // Los errores de permisos son esperados para perfiles no activados
    // No lanzar error para no interrumpir el flujo principal
  }
};

/**
 * Obtener logs de acceso de un WebID
 */
export const getAccessLogs = async (
  webId: string,
  days: number = 30
): Promise<AccessLog[]> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const logsQuery = query(
      accessLogsCollection,
      where('webId', '==', webId),
      where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    
    const snapshot = await getDocs(logsQuery);
    return snapshot.docs.map(doc => doc.data() as AccessLog);
  } catch (error) {
    console.error('Error getting access logs:', error);
    throw new Error('Error al obtener logs de acceso');
  }
};

/**
 * Registrar acción de usuario
 */
export const logUserAction = async (
  userId: string,
  action: string,
  resource: string,
  options: {
    resourceId?: string;
    details?: Record<string, any>;
    success?: boolean;
    errorMessage?: string;
  } = {}
): Promise<void> => {
  try {
    const logRef = doc(userActionLogsCollection);
    
    const userActionLog: UserActionLog = {
      id: logRef.id,
      userId,
      action,
      resource,
      resourceId: options.resourceId,
      details: options.details,
      ipAddress: undefined, // Se podría obtener del servidor
      userAgent: navigator.userAgent,
      timestamp: Timestamp.now(),
      success: options.success ?? true,
      errorMessage: options.errorMessage,
    };

    await setDoc(logRef, userActionLog);
    
    console.log('✅ User action log created:', logRef.id);
  } catch (error) {
    console.error('Error logging user action:', error);
    // No lanzar error para no interrumpir el flujo principal
  }
};

/**
 * Obtener logs de acciones de un usuario
 */
export const getUserActionLogs = async (
  userId: string,
  days: number = 30
): Promise<UserActionLog[]> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const logsQuery = query(
      userActionLogsCollection,
      where('userId', '==', userId),
      where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    
    const snapshot = await getDocs(logsQuery);
    return snapshot.docs.map(doc => doc.data() as UserActionLog);
  } catch (error) {
    console.error('Error getting user action logs:', error);
    throw new Error('Error al obtener logs de acciones');
  }
};

/**
 * Registrar inicio de sesión de página (para medir duración)
 */
export const startPageSession = (webId: string, accessType: AccessLog['accessType']): {
  endSession: () => Promise<void>;
} => {
  const startTime = Date.now();
  
  return {
    endSession: async () => {
      try {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        
        // Actualizar el log más reciente con la duración
        const recentLogsQuery = query(
          accessLogsCollection,
          where('webId', '==', webId),
          where('accessType', '==', accessType),
          orderBy('timestamp', 'desc'),
          limit(1)
        );
        
        const snapshot = await getDocs(recentLogsQuery);
        if (!snapshot.empty) {
          const logDoc = snapshot.docs[0];
          await updateDoc(logDoc.ref, { duration });
        }
      } catch (error) {
        // Solo mostrar warning si no es un error de permisos esperado
        if (error instanceof Error && !error.message.includes('insufficient permissions')) {
          console.warn('Error updating session duration:', error);
        }
        // Los errores de permisos son esperados para perfiles no activados
      }
    }
  };
};

/**
 * Obtener estadísticas de acceso
 */
export const getAccessStats = async (webId: string, days: number = 30): Promise<{
  totalAccesses: number;
  scanCount: number;
  viewCount: number;
  emergencyCount: number;
  uniqueVisitors: number;
  averageDuration: number;
}> => {
  try {
    const logs = await getAccessLogs(webId, days);
    
    const totalAccesses = logs.length;
    const scanCount = logs.filter(log => log.accessType === 'scan').length;
    const viewCount = logs.filter(log => log.accessType === 'view').length;
    const emergencyCount = logs.filter(log => log.emergencyContext).length;
    
    // Visitantes únicos (por IP o usuario)
    const uniqueIPs = new Set(logs.map(log => log.ipAddress || log.accessedBy).filter(Boolean));
    const uniqueVisitors = uniqueIPs.size;
    
    // Duración promedio
    const durationsWithValue = logs.filter(log => log.duration).map(log => log.duration!);
    const averageDuration = durationsWithValue.length > 0
      ? Math.floor(durationsWithValue.reduce((sum, duration) => sum + duration, 0) / durationsWithValue.length)
      : 0;
    
    return {
      totalAccesses,
      scanCount,
      viewCount,
      emergencyCount,
      uniqueVisitors,
      averageDuration,
    };
  } catch (error) {
    console.error('Error getting access stats:', error);
    throw new Error('Error al obtener estadísticas de acceso');
  }
};

/**
 * Limpiar logs antiguos (función para mantenimiento)
 */
export const cleanupOldLogs = async (daysToKeep: number = 90): Promise<void> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    // Esta función debería ejecutarse como Cloud Function programada
    console.log('Cleanup function should be implemented as a scheduled Cloud Function');
  } catch (error) {
    console.error('Error cleaning up old logs:', error);
    throw new Error('Error al limpiar logs antiguos');
  }
};
